<template>
  <v-dialog
    v-model="showUpdateDialog"
    persistent
    max-width="500"
    content-class="update-dialog"
  >
    <v-card elevation="10" shaped>
      <v-card-title class="headline primary--text text-center">
        <h4 class="mx-auto">{{ $t('version_check.title') }}</h4>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-4">
        <v-row dense>
          <v-col cols="12" sm="6" :class="isMobile ? 'text-center' : ''">
            <v-chip outlined color="grey" class="mb-2" :dense="isMobile">
              <v-icon left small>mdi-package-variant-closed</v-icon>
              {{ $t('version_check.current_version') }}: {{ currentVersion }}
            </v-chip>
          </v-col>

          <v-col cols="12" sm="6" :class="isMobile ? 'text-center' : ''">
            <v-chip color="primary" text-color="white" class="mb-3" :dense="isMobile">
              <v-icon left small>mdi-package-variant</v-icon>
              {{ $t('version_check.available_version') }}: {{ latestVersion }}
            </v-chip>
          </v-col>
        </v-row>

        <p>
          {{ $t('version_check.description') }}
        </p>
      </v-card-text>

      <v-card-actions :class="isMobile ? 'pb-4 px-4 d-flex flex-column align-center justify-center' : 'pb-4 px-4'">
        <v-spacer></v-spacer>

        <v-btn
          v-if="!forceUpdate"
          text
          color="grey darken-1"
          @click="closeDialog"
        >
          <v-icon left>mdi-clock-outline</v-icon>
          {{ $t('version_check.later') }}
        </v-btn>

        <v-btn
          color="success"
          elevation="2"
          large
          rounded
          class="px-5"
          @click="goToStore"
        >
          <v-icon left>mdi-download</v-icon>
          {{ $t('version_check.update_now') }}
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import appVersion from '@/version.js';
import { APP_VERSION_PATH, storeClientToken } from '@/utils/antiScrapeRetry';
import { deviceFromAppVersion } from '@/utils/deviceLock';

export default {
  name: 'AppVersionCheck',
  data() {
    return {
      currentVersion: '',
      latestVersion: '',
      showUpdateDialog: false,
      forceUpdate: false,
      appId: 'ru.rentprog', // https://play.google.com/store/apps/details?id=ru.rentprog
      appStoreId: '6444190825', // https://apps.apple.com/us/app/rentprog/id6444190825
    };
  },
  async created() {
    // Проверяем версию при запуске приложения
    await this.checkAppVersion();
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
  },
  methods: {
    async checkAppVersion() {
      // Получаем информацию о текущем приложении
      if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
        const appInfo = await App.getInfo();
        this.currentVersion = appInfo.version;
      } else {
        this.currentVersion = appVersion.version;
        this.forceUpdate = appVersion.forceUpdate;
      }

      // FT-018: API base через store getter (state.api_url → VUE_APP_API_URL → fallback)
      const url = `${this.$store.getters.getApiUrl}${APP_VERSION_PATH}`;
      this.axios.get(url).then(response => {
        // FT-186: выключатель нативной камеры — в vuex, его читает uppy_checkin.
        this.$store.commit("appVersion/SET_NATIVE_CAMERA", response.data.nativeCamera);
        // FT-191: состояние общего компьютера — в том же ответе (CTR-02).
        this.$store.dispatch("deviceLock/applyDevice", deviceFromAppVersion(response.data));
        this.latestVersion = response.data.version;
        this.forceUpdate = response.data.forceUpdate || false;
        // Сохраняем client_token для anti-scraping защиты. Через утилиту, а не
        // голым setItem: бросок (приватный Safari, переполненное хранилище)
        // улетал в общий .catch ниже и уносил с собой сравнение версий — то
        // есть force-update диалог такому пользователю не показывался НИКОГДА.
        storeClientToken(response.data.client_token);
        // Сравниваем версии — только для НАТИВНЫХ платформ (обновление через стор).
        // На web диалог не показываем: версия в базе = версия мобильного релиза
        // (может отставать от веб-деплоя), а веб обновляется сам через service
        // worker (AppUpdateNotifier + registerServiceWorker.js).
        if (Capacitor.getPlatform() !== 'web' &&
            this.compareVersions(this.latestVersion, this.currentVersion) > 0) {
          this.showUpdateDialog = true;
        }
      }).catch(error => {
        console.error('Error checking app version:', error);
      }).finally(() => {
        console.log('latestVersion', this.latestVersion, 'currentVersion', this.currentVersion, 'forceUpdate', this.forceUpdate);
      });
    },
    // Функция для сравнения версий в формате x.y.z
    compareVersions(v1, v2) {
      const parts1 = v1.split('.').map(Number);
      const parts2 = v2.split('.').map(Number);

      for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const part1 = parts1[i] || 0;
        const part2 = parts2[i] || 0;

        if (part1 > part2) return 1;
        if (part1 < part2) return -1;
      }

      return 0; // Версии равны
    },
    // Открытие магазина приложений для обновления
    async goToStore() {
      const platform = Capacitor.getPlatform();

      try {
        if (platform === 'android' && this.appId) {
          await Browser.open({ url: `market://details?id=${this.appId}` });
        } else if (platform === 'ios' && this.appStoreId) {
          await Browser.open({ url: `https://apps.apple.com/app/id${this.appStoreId}` });
        } else if (platform === 'web') {
          // Перезагрузка страницы с инвалидацией кэша
          await this.forceReloadWithoutCache();
        }
      } catch (error) {
        console.error('Ошибка открытия магазина приложений:', error);

        // Резервный вариант - обычная ссылка
        if (platform === 'ios') {
          window.open(`https://apps.apple.com/app/id${this.appStoreId}`, '_system');
        } else if (platform === 'android') {
          window.open(`https://play.google.com/store/apps/details?id=${this.appId}`, '_system');
        }
      }
    },
    async forceReloadWithoutCache() {
      try {
        // Очищаем кеш
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        }

        // Сохраняем текущий путь в localStorage, чтобы восстановить его после перезагрузки
        const currentPath = this.$router.currentRoute.fullPath;
        localStorage.setItem('lastRoute', currentPath);

        // Просто перезагружаем страницу без дополнительной навигации
        window.location.reload(true);
      } catch (error) {
        console.error('Ошибка при перезагрузке:', error);
        window.location.reload(true);
      }
    },
    closeDialog() {
      this.showUpdateDialog = false;
    }
  }
};
</script>
